import React, { useState, useEffect } from 'react';
import { Shield, Users, Award, Heart, Target, Eye, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { pagesAPI } from '../services/api';
import { useTheme } from '../contexts/ThemeContext';

const Sobre = () => {
  const [pageContent, setPageContent] = useState({});
  const [loading, setLoading] = useState(true);
  const { themeConfig } = useTheme();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const data = await pagesAPI.getByName('sobre');
      if (data && data.content) {
        setPageContent(data.content);
      }
    } catch (error) {
      console.error('Error loading page content:', error);
    } finally {
      setLoading(false);
    }
  };

  // Dynamic content with fallbacks
  const pageTitle = pageContent.pageTitle || 'Sobre a TÔ INDO ALI';
  const pageSubtitle = pageContent.pageSubtitle || 'Transformando sonhos em aventuras reais desde o nosso início. Somos apaixonados por turismo, natureza e por criar experiências que marcam vidas.';
  const historyTitle = pageContent.historyTitle || 'Nossa História';
  const historyText = pageContent.historyText || 'A TÔ INDO ALI - Turismo e Aventura nasceu da paixão por explorar o desconhecido e compartilhar as maravilhas naturais do Brasil e da América do Sul.\n\nCom anos de experiência no mercado de turismo, nossa equipe decidiu criar uma agência focada em proporcionar aventuras autênticas, onde cada viajante se conecta profundamente com a natureza, a cultura local e consigo mesmo.\n\nAcreditamos que viajar é mais do que visitar lugares - é viver experiências transformadoras. Por isso, cada roteiro é cuidadosamente elaborado para garantir segurança, conforto e momentos inesquecíveis.';
  const missionTitle = pageContent.missionTitle || 'Missão';
  const missionText = pageContent.missionText || 'Proporcionar experiências de viagem únicas e transformadoras, conectando pessoas com a natureza e culturas diversas, sempre com segurança, qualidade e respeito ao meio ambiente.';
  const visionTitle = pageContent.visionTitle || 'Visão';
  const visionText = pageContent.visionText || 'Ser referência em turismo de aventura e ecoturismo na América do Sul, reconhecida pela excelência no atendimento, compromisso com a sustentabilidade e por criar memórias inesquecíveis.';
  const valuesTitle = pageContent.valuesTitle || 'Nossos Valores';
  const cadasturNumber = pageContent.cadasturNumber || '46.231.427/0001-00';
  const differentialsTitle = pageContent.differentialsTitle || 'Nossos Diferenciais';
  const differentialsSubtitle = pageContent.differentialsSubtitle || 'O que nos torna especiais';

  // Dynamic values array
  const values = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: pageContent.value1Title || 'Segurança',
      description: pageContent.value1Desc || 'Priorizamos a segurança em todas as nossas expedições com seguros e equipes capacitadas'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: pageContent.value2Title || 'Paixão',
      description: pageContent.value2Desc || 'Amor pelo que fazemos e pelo turismo de aventura move cada uma de nossas ações'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: pageContent.value3Title || 'Respeito',
      description: pageContent.value3Desc || 'Respeito às comunidades locais, ao meio ambiente e à cultura de cada destino'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: pageContent.value4Title || 'Excelência',
      description: pageContent.value4Desc || 'Buscamos a excelência em cada detalhe para proporcionar experiências inesquecíveis'
    }
  ];

  // Dynamic differentials array
  const differentials = [
    pageContent.differential1,
    pageContent.differential2,
    pageContent.differential3,
    pageContent.differential4,
    pageContent.differential5,
    pageContent.differential6
  ].filter(d => d && d.trim());

  // Imagens da página - podem ser editadas no admin
  const sobreImages = pageContent.heroImages && pageContent.heroImages.length > 0
    ? pageContent.heroImages
    : [
        'https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=800&h=600&fit=crop',
        'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&h=600&fit=crop'
      ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#FFD700]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: themeConfig?.theme?.backgroundLight || '#f9fafb' }}>
      {/* Hero - Menor no mobile */}
      <div 
        className="text-white py-10 md:py-16"
        style={{ backgroundColor: themeConfig?.theme?.primary || '#003366' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 md:mb-4">{pageTitle}</h1>
          <p className="text-sm sm:text-base md:text-lg opacity-80 max-w-3xl">
            {pageSubtitle}
          </p>
        </div>
      </div>

      {/* Nossa História */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">{historyTitle}</h2>
              <div className="space-y-4 text-gray-700 leading-relaxed whitespace-pre-line">
                {historyText.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="relative h-96 rounded-lg overflow-hidden shadow-2xl">
              <img
                src={sobreImages[0]}
                alt="Aventura na natureza"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Missão e Visão */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="border-t-4 border-[#FFD700]">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center">
                    <Target className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{missionTitle}</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {missionText}
                </p>
              </CardContent>
            </Card>

            <Card className="border-t-4 border-[#FFD700]">
              <CardContent className="p-8">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 bg-[#FFD700] rounded-full flex items-center justify-center">
                    <Eye className="w-6 h-6 text-gray-900" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900">{visionTitle}</h3>
                </div>
                <p className="text-gray-700 leading-relaxed">
                  {visionText}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{valuesTitle}</h2>
            <p className="text-xl text-gray-600">Princípios que guiam cada uma de nossas ações</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FFD700] rounded-full mb-4">
                    <div className="text-gray-900">
                      {value.icon}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Diferenciais */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">{differentialsTitle}</h2>
            <p className="text-xl text-gray-600">{differentialsSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {differentials.length > 0 ? (
              differentials.map((diff, index) => (
                <div key={index} className="flex items-start space-x-3 bg-gray-50 p-6 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{diff}</p>
                </div>
              ))
            ) : (
              // Fallback defaults if no differentials are set
              ['Seguro viagem incluso', 'Guias certificados pelo Cadastur', 'Roteiros exclusivos', 'Atendimento 24h durante a viagem', 'Grupos pequenos e personalizados', 'Experiência e paixão por aventura'].map((diff, index) => (
                <div key={index} className="flex items-start space-x-3 bg-gray-50 p-6 rounded-lg">
                  <CheckCircle className="w-6 h-6 text-[#FFD700] flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{diff}</p>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Cadastur - Usando cores do tema (mesma cor do rodapé = primária) */}
      <section 
        className="py-12 text-white"
        style={{ backgroundColor: themeConfig?.theme?.primary || '#003366' }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg" style={{ color: themeConfig?.theme?.footerText || '#d1d5db' }}>
            Empresa registrada no <span className="font-bold" style={{ color: themeConfig?.theme?.secondary || '#FFD700' }}>Cadastur</span> - Ministério do Turismo
          </p>
          <p className="mt-2" style={{ color: themeConfig?.theme?.footerText || '#d1d5db', opacity: 0.8 }}>CNPJ: {cadasturNumber}</p>
        </div>
      </section>
    </div>
  );
};

export default Sobre;
